import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface Category {
  id?: string;
  name_de: string;
  name_en: string;
  icon: string;
  sort_order: number;
  active: boolean;
}

interface CategoryFormModalProps {
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
}

const ICON_OPTIONS = [
  'ri-restaurant-line',
  'ri-cake-line',
  'ri-bowl-line',
  'ri-cup-line',
  'ri-leaf-line',
  'ri-fire-line',
  'ri-star-line',
  'ri-heart-line',
  'ri-shopping-basket-line',
  'ri-bread-line',
];

const emptyCategory = (): Category => ({
  name_de: '',
  name_en: '',
  icon: 'ri-restaurant-line',
  sort_order: 0,
  active: true,
});

const CategoryFormModal = ({ category, onClose, onSaved }: CategoryFormModalProps) => {
  const isEdit = !!category?.id;
  const [form, setForm] = useState<Category>(category ?? emptyCategory());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(category ?? emptyCategory());
    setError(null);
  }, [category]);

  const set = (field: keyof Category, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name_de.trim()) { setError('Kategori adı (Almanca) zorunludur.'); return; }

    setSaving(true);
    setError(null);

    const payload = {
      name_de: form.name_de.trim(),
      name_en: form.name_en.trim() || form.name_de.trim(),
      icon: form.icon,
      sort_order: Number(form.sort_order),
      active: form.active,
    };

    let err;
    if (isEdit) {
      ({ error: err } = await supabase.from('categories').update(payload).eq('id', category!.id!));
    } else {
      ({ error: err } = await supabase.from('categories').insert(payload));
    }

    setSaving(false);
    if (err) { setError('Kayıt sırasında hata: ' + err.message); return; }
    onSaved();
  };

  const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all';
  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">
            {isEdit ? 'Kategoriyi Düzenle' : 'Yeni Kategori'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Ad (Almanca) *</label>
              <input
                type="text"
                value={form.name_de}
                onChange={(e) => set('name_de', e.target.value)}
                placeholder="z.B. Pizza"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Ad (İngilizce)</label>
              <input
                type="text"
                value={form.name_en}
                onChange={(e) => set('name_en', e.target.value)}
                placeholder="e.g. Pizza"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>İkon</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => set('icon', icon)}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all cursor-pointer
                    ${form.icon === icon ? 'border-orange-500 bg-orange-50 text-orange-500' : 'border-gray-200 text-gray-500 hover:border-orange-300'}`}
                >
                  <i className={`${icon} text-lg`}></i>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Sıralama</label>
            <input
              type="number"
              min="0"
              value={form.sort_order}
              onChange={(e) => set('sort_order', e.target.value)}
              className={inputCls}
            />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <div
              onClick={() => set('active', !form.active)}
              className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer ${form.active ? 'bg-orange-500' : 'bg-gray-200'}`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.active ? 'left-5' : 'left-1'}`} />
            </div>
            <span className="text-sm font-medium text-gray-700">Aktif</span>
          </label>

          {error && (
            <div className="flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg bg-red-50 text-red-600">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line"></i>
              </div>
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-save-line"></i>
                </div>
              )}
              {isEdit ? 'Güncelle' : 'Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryFormModal;
